import { useId } from 'react'
import PropTypes from 'prop-types'
import { Accordion, AccordionDetails, AccordionSummary, Box, ButtonBase, FormControlLabel, Stack, Switch, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import SmartSelect from '../../../generic/smartSelect/SmartSelect.jsx'
import { QR_ACTIONS } from './qrLookupApi'
import { JOB_ORDER_CRITERIA, QR_ACTION_FIELD, QR_JOB_DETAIL_FIELDS, QR_READING_FIELDS, readQrLookupConfig } from './qrLookupConfig'

/**
 * The gear of the QR Lookup widget.
 *
 * Its own form and not the base one: the base paints every field of getConfigurationInfo() always,
 * and the options of the job detail only mean something with that action (RN2). The base render is
 * private, so the original fields are drawn here too, with the same keys and the same defaults —
 * what a saved widget holds is untouched.
 *
 * Laid out for whoever sets a station up (RN31): how a label is read is set once and folded away
 * under "Reading"; what a read opens is the choice that matters, so it is two cards that say what
 * each one does; and with the job detail, each option of "How the job is chosen" explains itself and
 * the block ends with the sentence the station will live by.
 */

// "Reading an FO here opens: the job running on this machine → … → otherwise a notice …". The steps
// follow the cascade: the active job always comes first and the notice always exists; the order and
// the fallback change the middle, and asking the operator adds a coda.
const summaryOf = (config, t_) => {
  const steps = [
    t_('the job running on this machine'),
    config.jobOrderCriterion === JOB_ORDER_CRITERIA.PRIORITY ? t_('the first one planned here by priority') : t_('the next one planned here'),
    ...(config.allowTechnologyFallback !== false ? [t_('one this machine can do')] : []),
    t_('otherwise a notice with the machine to go to')
  ]
  const choice = config.askOperatorOnMultipleJobs === true ? ' · ' + t_('if there are several, the operator chooses') : ''
  return t_('Reading an FO here opens:') + ' ' + steps.join(' → ') + choice
}

const HelpLine = ({ id, text, indent = false }) => (
  <Typography id={id} variant='caption' color='text.secondary' sx={{ display: 'block', lineHeight: 1.4, pl: indent ? '3.6rem' : 0 }}>
    {text}
  </Typography>
)

const ConfigField = ({ field, value, onChange, t_ }) => {
  const helpId = useId()
  const help = field.help ? t_(field.help) : ''

  if (field.type === 'boolean') {
    return (
      <Box data-config-key={field.key}>
        <FormControlLabel
          control={(
            <Switch
              checked={value === true}
              onChange={(event) => onChange(event.target.checked)}
              inputProps={help !== '' ? { 'aria-describedby': helpId } : {}}
            />
          )}
          label={t_(field.label)}
        />
        {help !== '' && <HelpLine id={helpId} text={help} indent />}
      </Box>
    )
  }
  const optionLabels = field.optionLabels ?? {}
  return (
    <Box data-config-key={field.key}>
      <SmartSelect
        selectableOptions={field.selectableOptions}
        label={t_(field.label)}
        value={value}
        onChange={onChange}
        optionLabelTransform={(option) => t_(optionLabels[option] ?? option)}
      />
      {help !== '' && <Box sx={{ mt: 0.5 }}><HelpLine id={helpId} text={help} /></Box>}
    </Box>
  )
}

// One card per action, read as a radio group: the selected one stands out by its border and its
// background, not only by colour, so it reads on a washed-out plant screen too.
const ActionCards = ({ value, onChange, t_ }) => {
  const labelId = useId()
  const options = QR_ACTION_FIELD.selectableOptions
  const move = (step) => onChange(options[(options.indexOf(value) + step + options.length) % options.length])
  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') move(1)
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') move(-1)
    else return
    event.preventDefault()
  }

  return (
    <Box data-gear-part='action' data-config-key={QR_ACTION_FIELD.key}>
      <Typography id={labelId} variant='subtitle2' color='text.secondary' sx={{ mb: 1 }}>{t_(QR_ACTION_FIELD.label)}</Typography>
      <Stack role='radiogroup' aria-labelledby={labelId} gap={1} onKeyDown={onKeyDown}>
        {options.map((option) => (
          <ActionCard
            key={option}
            checked={option === value}
            title={t_(QR_ACTION_FIELD.optionLabels[option])}
            description={t_(QR_ACTION_FIELD.optionHelp[option])}
            onSelect={() => onChange(option)}
          />
        ))}
      </Stack>
    </Box>
  )
}

const ActionCard = ({ checked, title, description, onSelect }) => {
  const titleId = useId()
  const descriptionId = useId()
  return (
    <ButtonBase
      role='radio'
      aria-checked={checked}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      tabIndex={checked ? 0 : -1}
      onClick={onSelect}
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        textAlign: 'left',
        gap: 0.25,
        px: 1.5,
        py: 1.25,
        borderRadius: '0.5rem',
        border: '1px solid',
        borderColor: checked ? theme.palette.primary.main : theme.palette.divider,
        backgroundColor: checked ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
        '&:focus-visible': { outline: '2px solid ' + theme.palette.primary.main, outlineOffset: '2px' }
      })}
    >
      <Typography id={titleId} sx={{ fontWeight: checked ? 700 : 500 }}>{title}</Typography>
      <Typography id={descriptionId} variant='caption' color='text.secondary'>{description}</Typography>
    </ButtonBase>
  )
}

const QrLookupWidgetConfiguration = ({ data = {}, setData, t_ }) => {
  const blockTitleId = useId()
  const readingId = useId()
  const config = readQrLookupConfig(data)
  const update = (key, value) => setData({ ...(data ?? {}), [key]: value })
  const renderField = (field) => (
    <ConfigField
      key={field.key}
      field={field}
      value={config[field.key]}
      onChange={(value) => update(field.key, value)}
      t_={t_}
    />
  )

  // The Popover of WidgetContainer is as wide as what it holds: without a bound, the summary and the
  // help lines stretch it to the screen width. 30rem keeps them to a reading line.
  return (
    <Stack sx={{ maxWidth: '30rem', padding: '1rem', paddingTop: 0, backgroundColor: 'background.paper' }}>
      <Typography component='h3' variant='h6' sx={{ my: 2 }}>{t_('Configuration')}</Typography>
      <Stack gap={3}>
        {/* Folded on opening: set once per station, and its values are saved the same either way. */}
        <Accordion data-gear-part='reading' disableGutters variant='outlined' sx={{ backgroundColor: 'transparent' }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} aria-controls={readingId + '-content'} id={readingId}>
            <Typography>{t_('Reading')}</Typography>
          </AccordionSummary>
          <AccordionDetails id={readingId + '-content'}>
            <Stack gap={3}>{QR_READING_FIELDS.map(renderField)}</Stack>
          </AccordionDetails>
        </Accordion>

        <ActionCards value={config.action} onChange={(value) => update(QR_ACTION_FIELD.key, value)} t_={t_} />

        {config.action === QR_ACTIONS.JOB_DETAIL && (
          <Stack
            data-gear-part='job'
            role='group'
            aria-labelledby={blockTitleId}
            gap={2.5}
            sx={{ p: 2, borderRadius: '0.5rem', backgroundColor: 'action.hover' }}
          >
            <Typography id={blockTitleId} variant='subtitle2' color='text.secondary'>
              {t_('How the job is chosen')}
            </Typography>
            {QR_JOB_DETAIL_FIELDS.map(renderField)}
            <Typography variant='body2' color='text.secondary' sx={{ pt: 1.5, borderTop: 1, borderColor: 'divider', lineHeight: 1.5 }}>
              {summaryOf(config, t_)}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}

QrLookupWidgetConfiguration.propTypes = {
  /** The saved configuration; keys it lacks are shown at their defaults. */
  data: PropTypes.object,
  setData: PropTypes.func.isRequired,
  t_: PropTypes.func.isRequired
}

export default QrLookupWidgetConfiguration
